import type {ClassicCard as BrazeContentCard} from "../services/sdk"

/**
 * The Braze content card, stripped of methods and required ID
 */
export type SimpleBrazeContentCard = Omit<
  BrazeContentCard,
  "id" | "removeSubscription" | "removeAllSubscriptions" | "subscribeToClickedEvent" | "subscribeToDismissedEvent" | "dismissCard"
> & {id: string}

export type Component = Banner | Popup

export const hideIf = ["viewed", "clicked", "dismissed"] as const
/**
 * Implemented by all Content Cards
 * the properties are available to all braze content cards
 */
interface ComponentBase {
  id: string
  hideIf?: typeof hideIf[number]
}

export interface Banner extends ComponentBase {
  title: string
  position: "top"
  subtitle?: string
  ctaCopy: string
  backgroundColor1?: string
  backgroundColor2?: string
  icon: string
  ctaUrlExternal?: boolean
  ctaUrl?: string // /wallet
  /**
   * Subsequent component to render when this banner is clicked
   */
  linkedComponent?: ContentCardID
}
export interface Popup extends ComponentBase {
  subtitle?: string
  icon: string
  message?: string
  ctaCopy: string
  ctaUrl: string
  ctaUrlExternal?: boolean
  title: string
  headerIcon?: string

  /**
   * Subsequent component to render when this notification is clicked
   */
  linkedComponent?: ContentCardID
}

// Classic Notification from Braze
export interface BrazeNotification {
  background: string
  image_shape: PBContentCardExtrasImageShape
  badge: PBContentCardExtrasBadge // "PBContentCardExtrasBadge | string"
  // native supports badge as straight http image urls, web does not. Product doesn't currently need this feature
  // leaving typing out for now
  campaign_name: string
  entity_id: string
  perkbox_product: string
}

enum PBContentCardExtrasImageShape {
  Square = "square",
  Round = "round"
}

enum PBContentCardExtrasBadge {
  Perks = "perks",
  Celebration = "celebration",
  Warning = "warning",
  Wellness = "wellness",
  Reward = "reward",
  Admin = "admin"
}

export const contentCardIds = ["popup", "banner"] as const
export type ContentCardID = typeof contentCardIds[number]

/** these are limited based on what Native currenly suports */
export enum ContentCardScreen {
  home = "home",
  perks = "perks/discover",
  rewards = "rewards/categories",
  celebration = "recognition/polls",
  wellness = "wellness"
}

export enum ContentCardPosition {
  top = "top"
}

export type PBEnhancedExtras = Prettify<{
  id: string
  component_id: ContentCardID
  showOnPages?: ContentCardScreen[]
  priority: number
  banner?: Banner
  popup?: Popup
}>

/** TODO create type that has removed properties gone */
export type PBEnhancedContentCard = Prettify<
  Omit<SimpleBrazeContentCard, "extras"> & {
    extras: PBEnhancedExtras
  }
>

/**
 * ContentCard with a banner component
 */
export type ContentCardBanner = Omit<PBEnhancedContentCard, "extras"> & {extras: WithRequired<PBEnhancedExtras, "banner">}

export type ContentCard = PBEnhancedContentCard

export type LegacyNotificationCard = SimpleBrazeContentCard
